/**
 * Word-level diff of two plain strings — the detail `diffBlocks` cannot give.
 *
 * `diffBlocks` (./diff.ts) reports "this block changed"; it deliberately says
 * nothing about WHAT changed, because block identity is about structure, not
 * wording. This module fills that gap for the one field that carries wording:
 * a block's `text`. Never fed `runs` — formatting-only edits (bold, italic)
 * must not read as a wording change, which is exactly the invariant
 * `documents.ts`'s `runsMatchText` refinement protects on the schema side.
 *
 * This package has no package.json and adds no dependency (see index.ts's
 * doc comment) — this is a hand-rolled LCS, not a pulled-in diff library.
 */

export type WordDiffOp = { kind: 'same' | 'added' | 'removed'; text: string };

/**
 * A merge token — `{{fieldName}}`, optionally with inner whitespace, the same
 * shape `documents.ts`'s `MERGE_TOKEN` regex resolves — is one indivisible
 * token here. Without this alternative first in `TOKEN_RE`, `\S+` would slice
 * `{{company_legal_name}}` into `{{company_legal_name}}`'s underscores are
 * fine (no whitespace) but a token like `{{ company_legal_name }}` (inner
 * spaces, which the renderer also accepts) would fall apart into
 * `{{`, `company_legal_name`, `}}` and a variable swap would read as three
 * unrelated word changes instead of one field replacement.
 */
const TOKEN_RE = /\{\{\s*[a-zA-Z][a-zA-Z0-9_]*\s*\}\}|\S+|\s+/g;

/**
 * Above this many tokens on EITHER side, the LCS table below is an
 * `(n+1) x (m+1)` grid — at the threshold that is ~25 million cells, well
 * into "freezes the tab" territory for a synchronous diff with no worker.
 * 5,000 tokens is many thousands of words: no real clause, paragraph or
 * callout in a legal document gets remotely close, so the fallback below
 * only ever fires on pathological input (e.g. a whole document pasted into
 * one block), never on real contract prose.
 */
const MAX_TOKENS = 5000;

/**
 * Split into merge-token / non-whitespace-run / whitespace-run pieces. The
 * three alternatives are exhaustive over every character (a character is
 * either whitespace or not), so `tokenize(s).join('') === s` always — that is
 * what makes reconstruction from the ops exact rather than approximate.
 */
function tokenize(text: string): string[] {
  if (text === '') return [];
  return text.match(TOKEN_RE) ?? [];
}

/** Word-level diff of two plain strings. */
export function diffWords(before: string, after: string): WordDiffOp[] {
  if (before === after) {
    return before === '' ? [] : [{ kind: 'same', text: before }];
  }

  const a = tokenize(before);
  const b = tokenize(after);

  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    const ops: WordDiffOp[] = [];
    if (before !== '') ops.push({ kind: 'removed', text: before });
    if (after !== '') ops.push({ kind: 'added', text: after });
    return ops;
  }

  const n = a.length;
  const m = b.length;

  // Standard LCS length table over tokens, built bottom-up so the backtrack
  // below can walk forward from (0, 0) deciding same/removed/added greedily.
  const lcs: Uint32Array[] = new Array(n + 1);
  for (let i = 0; i <= n; i += 1) lcs[i] = new Uint32Array(m + 1);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j]
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const ops: WordDiffOp[] = [];
  const push = (kind: WordDiffOp['kind'], text: string) => {
    const last = ops[ops.length - 1];
    if (last && last.kind === kind) last.text += text;
    else ops.push({ kind, text });
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push('same', a[i]);
      i += 1; j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      // Preferring "removed" on ties is what makes a straight word
      // replacement (test: "the red fox" -> "the blue fox") read as
      // removed-then-added adjacently, rather than added-then-removed.
      push('removed', a[i]);
      i += 1;
    } else {
      push('added', b[j]);
      j += 1;
    }
  }
  while (i < n) { push('removed', a[i]); i += 1; }
  while (j < m) { push('added', b[j]); j += 1; }

  return ops;
}
